/**
 * Hook for idle session timeout
 *
 * Monitors user activity (mouse, keyboard, touch, scroll, click) and
 * automatically calls onTimeout after a configurable period of inactivity.
 * Coordinates across browser tabs via localStorage so that activity in
 * any tab resets the timer in all tabs.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

import { useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'idle-last-activity';
const THROTTLE_MS = 30_000; // 30 seconds

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

interface UseIdleTimeoutOptions {
  timeoutMs: number;
  onTimeout: () => void;
}

/**
 * Checks whether localStorage is available and writable.
 * Returns false in private browsing mode or when storage is disabled.
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__idle_timeout_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

export function useIdleTimeout({ timeoutMs, onTimeout }: UseIdleTimeoutOptions): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWriteRef = useRef<number>(0);
  const onTimeoutRef = useRef(onTimeout);
  const storageAvailableRef = useRef<boolean>(false);

  // Keep the callback ref current so the timer always calls the latest version
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Check localStorage availability once on mount
  useEffect(() => {
    storageAvailableRef.current = isLocalStorageAvailable();
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  const handleActivity = useCallback(() => {
    const now = Date.now();

    // Throttle localStorage writes to once per 30 seconds
    if (now - lastWriteRef.current >= THROTTLE_MS) {
      lastWriteRef.current = now;
      if (storageAvailableRef.current) {
        try {
          localStorage.setItem(STORAGE_KEY, String(now));
        } catch {
          // Storage may become unavailable at runtime — fall back silently
        }
      }
    }

    resetTimer();
  }, [resetTimer]);

  const handleStorageEvent = useCallback(
    (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue !== null) {
        resetTimer();
      }
    },
    [resetTimer],
  );

  useEffect(() => {
    // Start the initial timer
    resetTimer();

    // Attach activity listeners to document
    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity, { passive: true });
    });

    // Listen for cross-tab activity via storage events
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      // Clean up timer
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      // Clean up activity listeners
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, handleActivity);
      });

      // Clean up storage listener
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [resetTimer, handleActivity, handleStorageEvent]);
}
